// Pure step sequencing for the in-app guide: which step shows, when it is done, and what is kept per
// browser. No DOM here, so tests/unit/ns-tutorial covers it; the overlay and service do the drawing.

export type TourId = 'onboarding' | 'welcome' | 'workflows' | 'account';

// The tours that show a page around, which My Account's switch brings back
export const PAGE_TOURS: TourId[] = ['welcome', 'workflows', 'account'];

// What the page tells the guide when something really happened (an ns-tutorial-signal event)
export type TutorialSignal =
  | { type: 'stage'; stage: string; enrolledCompany?: boolean; companyName?: string }
  | { type: 'onboarding-complete' }
  | { type: 'job-opened' }
  | { type: 'job-closed' }
  | { type: 'workflow-opened' }
  | { type: 'workflow-closed' };

// Facts a step's wording or `when` depends on
export interface TourContext {
  enrolledCompany?: boolean;
  companyName?: string;
  hasJob?: boolean;
  hasWorkflow?: boolean;
  // Optional steps whose section isn't on the page for this user: left out, and out of the count
  absent?: string[];
}

export type BubblePlacement = 'top' | 'top-start' | 'bottom' | 'bottom-start' | 'left' | 'right';

export interface TourStep {
  id: string;
  // data-tour-id values; each gets its own hole in the scrim
  targets: string[];
  // The data-tour-id the bubble points at, when not the first target
  anchor?: string;
  // Where the bubble sits beside its anchor (below by default), when that would cover what the user
  // needs to reach
  placement?: BubblePlacement;
  text: string;
  // 'action': moves on when the page signals the real action succeeded. 'next': the bubble's Next.
  advance: 'action' | 'next';
  // Onboarding steps belong to one stage card and show only while it is the current stage
  stage?: string;
  // The cut-out shows but swallows clicks, for steps that explain rather than ask
  passive?: boolean;
  // data-tour-id values whose click hides the bubble until the step's signal arrives (a failed
  // action leaves it hidden; success moves on)
  hideOn?: string[];
  // Signals besides Next that complete the step. A 'stage' signal completes only when it names a
  // different stage from the step's own.
  doneOn?: TutorialSignal['type'][];
  when?: (context: TourContext) => boolean;
  // What Next does before moving on
  onNext?: 'open-first-job' | 'open-first-workflow';
  // A data-tour-id the guide clicks before resolving the step's targets: a tab the step is about
  select?: string;
  // A section only some people see: if it isn't on the page, the step is left out after a short wait
  optional?: boolean;
  // Steps on the same view (a tab, an opened workflow): when the first of them shows, the guide checks
  // which of the group's optional sections are on the page, so the count changes only there
  group?: string;
  // For an optional step: a data-tour-id that renders in the same update as its section (a table and
  // its rows, say). Once that's on the page the section is there or not, so the group's check doesn't
  // wait for it.
  decidedBy?: string;
}

export interface Tour {
  id: TourId;
  steps: TourStep[];
}

// One localStorage key, NS_TUTORIAL_STATE
export interface StoredTutorialState {
  // End tour: the whole walkthrough is off until My Account turns it back on
  off: boolean;
  // Finished or skipped tours
  done: TourId[];
  // Finished steps of tours still running
  stepsDone: Partial<Record<TourId, string[]>>;
}

export const STORAGE_KEY = 'NS_TUTORIAL_STATE';

export function emptyState(): StoredTutorialState {
  return { off: false, done: [], stepsDone: {} };
}

// Anything unreadable starts again from nothing rather than breaking the app
export function parseStoredState(raw: string | null | undefined): StoredTutorialState {
  if (!raw) return emptyState();
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return emptyState();
    const done = Array.isArray(parsed.done) ? parsed.done.filter((id: unknown) => typeof id === 'string') : [];
    const stepsDone: StoredTutorialState['stepsDone'] = {};
    if (parsed.stepsDone && typeof parsed.stepsDone === 'object') {
      for (const [tourId, ids] of Object.entries(parsed.stepsDone)) {
        if (Array.isArray(ids)) stepsDone[tourId as TourId] = ids.filter((id) => typeof id === 'string');
      }
    }
    return { off: parsed.off === true, done, stepsDone };
  } catch {
    return emptyState();
  }
}

export function isTourDone(state: StoredTutorialState, tourId: TourId): boolean {
  return state.done.includes(tourId);
}

export function shouldRun(state: StoredTutorialState, tourId: TourId): boolean {
  return !state.off && !isTourDone(state, tourId);
}

export function visibleSteps(tour: Tour, context: TourContext): TourStep[] {
  return tour.steps.filter((step) => (!step.when || step.when(context)) && !context.absent?.includes(step.id));
}

function stepsDoneFor(state: StoredTutorialState, tourId: TourId): string[] {
  return state.stepsDone[tourId] ?? [];
}

// The step to show now, or null. An action tour shows the first unfinished step of the current
// stage, so going back to a stage whose step is finished shows nothing; a Next tour shows its first
// unfinished step.
export function currentStep(
  tour: Tour,
  state: StoredTutorialState,
  context: TourContext,
  stage?: string,
): TourStep | null {
  if (!shouldRun(state, tour.id)) return null;
  const finished = stepsDoneFor(state, tour.id);
  const pending = visibleSteps(tour, context).filter((step) => !finished.includes(step.id));
  if (tour.steps.some((step) => step.stage)) return pending.find((step) => step.stage === stage) ?? null;
  return pending[0] ?? null;
}

// "n of N" for the bubble
export function stepPosition(tour: Tour, context: TourContext, stepId: string): { index: number; total: number } {
  const steps = visibleSteps(tour, context);
  return { index: steps.findIndex((step) => step.id === stepId) + 1, total: steps.length };
}

export function signalCompletes(step: TourStep, signal: TutorialSignal): boolean {
  if (!step.doneOn?.includes(signal.type)) return false;
  return signal.type !== 'stage' || signal.stage !== step.stage;
}

// Marks a step finished, and the tour too once every step it shows is finished
export function completeStep(
  state: StoredTutorialState,
  tour: Tour,
  context: TourContext,
  stepId: string,
): StoredTutorialState {
  const finished = stepsDoneFor(state, tour.id);
  const stepsDone = finished.includes(stepId) ? finished : [...finished, stepId];
  const next = { ...state, stepsDone: { ...state.stepsDone, [tour.id]: stepsDone } };
  return visibleSteps(tour, context).every((step) => stepsDone.includes(step.id)) ? finishTour(next, tour.id) : next;
}

// Skip section, the last step, or onboarding finishing: the tour never shows again in this browser
export function finishTour(state: StoredTutorialState, tourId: TourId): StoredTutorialState {
  const stepsDone = { ...state.stepsDone };
  delete stepsDone[tourId];
  return { ...state, done: isTourDone(state, tourId) ? state.done : [...state.done, tourId], stepsDone };
}

// Marks steps unfinished again, so the tour goes back to the first of them
export function reopenSteps(state: StoredTutorialState, tourId: TourId, stepIds: string[]): StoredTutorialState {
  const stepsDone = stepsDoneFor(state, tourId).filter((id) => !stepIds.includes(id));
  return { ...state, stepsDone: { ...state.stepsDone, [tourId]: stepsDone } };
}

export function endTour(state: StoredTutorialState): StoredTutorialState {
  return { ...state, off: true };
}

// My Account's "Show walkthrough" switch. Turning it on brings the welcome tour and the page tours
// back from their start; onboarding stays finished, since there's nothing left to guide.
export function setShown(state: StoredTutorialState, shown: boolean): StoredTutorialState {
  if (!shown) return endTour(state);
  const stepsDone = { ...state.stepsDone };
  for (const id of PAGE_TOURS) delete stepsDone[id];
  return { off: false, done: state.done.filter((id) => !PAGE_TOURS.includes(id)), stepsDone };
}

// Every data-tour-id a step needs on the page before it shows: its targets, its anchor and the
// buttons that hide it
export function stepTourIds(step: TourStep): string[] {
  return [...new Set([...step.targets, ...(step.anchor ? [step.anchor] : []), ...(step.hideOn ?? [])])];
}

// Every data-tour-id a tour names, the tabs it selects included, for the check that they're all in the app
export function tourIds(tour: Tour): string[] {
  return [...new Set(tour.steps.flatMap((step) => [...stepTourIds(step), ...(step.select ? [step.select] : [])]))];
}

export function fillText(text: string, context: TourContext): string {
  return text.replace(/COMPANY_NAME/g, context.companyName || 'your company');
}

// How long the guide waits for a step's targets before skipping the step
export const TARGET_TIMEOUT_MS = 5000;

// How long an optional step waits for its section, which shows only for some people, before it's left
// out. Long enough for a tab's data to load.
export const OPTIONAL_TIMEOUT_MS = 2500;

export type TargetOutcome = 'show' | 'wait' | 'skip';

// Every target found and still (not sliding in, say) shows the step; one still moving waits, but is
// shown anyway once the timeout passes. Some still missing waits until the timeout, then skips it.
export function targetOutcome(
  found: number,
  wanted: number,
  elapsedMs: number,
  settled = true,
  timeoutMs = TARGET_TIMEOUT_MS,
): TargetOutcome {
  const timedOut = elapsedMs >= timeoutMs;
  if (found >= wanted) return settled || timedOut ? 'show' : 'wait';
  return timedOut ? 'skip' : 'wait';
}
